import { LINKS, CAMPAIGN_IMAGES } from "../../config/links";

const PAINS = [
  {
    title: "Leads come in… then disappear",
    copy: "They fill out a form. Then vanish. You never hear from them again.",
  },
  {
    title: "Prospects say \"I'll think about it\" — then ghost",
    copy: "You leave the call hopeful. You never see them again.",
  },
  {
    title: "You forget to follow up because you're busy",
    copy: "You're running a business, not a memory game. Leads slip through the cracks.",
  },
  {
    title: "Your pipeline looks full — but revenue doesn't reflect it",
    copy: "Lots of names. Few closes. Calendars full of no-shows.",
  },
];

export default function Problem() {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="relative pt-24 pb-16 sm:pt-32 sm:pb-20 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* LEFT — Image */}
          <div className="lg:col-span-5 reveal lg:sticky lg:top-28">
            <div className="mb-4 inline-flex items-center gap-2">
              <span className="font-sans-body uppercase text-[10px] tracking-[0.3em] text-amber-400 bg-amber-500/10 border border-amber-500/30 px-3 py-1.5">
                The Reality
              </span>
            </div>

            <div className="relative overflow-hidden border border-white/10 bg-[#0a1733]">
              <img
                src={CAMPAIGN_IMAGES.sinkingStrategy}
                alt="Your lead strategy right now: sinking"
                className="w-full h-auto object-contain"
              />
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0a1733]/70 to-transparent pointer-events-none" />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/5 pointer-events-none" />
            </div>

            {/* Closing statement block */}
            <div className="mt-8 p-6 border border-amber-500/30 bg-amber-500/[0.04]">
              <div className="font-sans-body uppercase text-[10px] tracking-[0.3em] text-amber-400 mb-2">
                The Truth
              </div>
              <p className="font-display text-xl sm:text-2xl text-white leading-snug">
                This isn't a{" "}
                <span className="italic text-slate-400">lead</span> problem.
                It's a{" "}
                <span className="italic text-amber-400">system</span> problem.
              </p>
            </div>
          </div>

          {/* RIGHT — Pain points */}
          <div className="lg:col-span-7">
            <div className="reveal flex items-center gap-3 mb-6">
              <span className="h-[1px] w-10 bg-amber-500/70" />
              <span className="font-sans-body uppercase text-[11px] tracking-[0.32em] text-amber-400">
                If You're an Advisor
              </span>
            </div>

            <h2
              data-testid="problem-headline"
              className="reveal font-display font-light text-white text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.01em] text-balance"
              style={{ transitionDelay: "80ms" }}
            >
              This probably sounds{" "}
              <span className="italic text-amber-400">familiar.</span>
            </h2>

            <ul className="mt-10 space-y-3">
              {PAINS.map((p, i) => (
                <li
                  key={p.title}
                  data-testid={`problem-item-${i}`}
                  className="reveal group bg-[#0e1e44]/70 border border-white/10 hover:border-amber-500/40 p-6 transition-all duration-300 hover:-translate-y-0.5"
                  style={{ transitionDelay: `${180 + i * 70}ms` }}
                >
                  <div className="flex items-start gap-5">
                    <span className="font-display text-3xl text-amber-500/70 leading-none mt-1 shrink-0">
                      0{i + 1}
                    </span>
                    <div>
                      <h3 className="font-display text-xl sm:text-2xl text-white leading-tight">
                        {p.title}
                      </h3>
                      <p className="mt-2 text-slate-400 text-sm leading-relaxed">
                        {p.copy}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <div className="reveal mt-10">
              <a
                href={LINKS.calendar}
                data-testid="problem-cta"
                className="group inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-500 text-slate-950 font-semibold px-8 py-4 text-[13px] uppercase tracking-[0.18em] transition-all duration-300 hover:-translate-y-0.5"
              >
                I Need a Better System
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="transition-transform group-hover:translate-x-1">
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
